import { removeDiacritics } from '#src/app/shared/utils/string';

import type { DecisionMakerHitData, DecisionMakerMatch, LinkableDecisionMakerHitData } from './types';

export type DecisionMakerMatchRules = {
	// longer queries are topics ("stop the new highway through our town"), not names
	maxQueryWords: number;
	fullName: {
		// petitions needed relative to the next decision maker with the same name
		minLeadRatio: number;
	};
	surnameOnly: {
		targetableTypes: readonly string[];
		// longer names are organizations ("People for the Ethical Treatment of Animals"), which a topic word
		// like "animals" shouldn't name
		maxNameWords: number;
		minPetitions: number;
		requireVerified: boolean;
		// namesakes with at least 1/minLeadRatio of the top one's petitions are shown side by side
		minLeadRatio: number;
		// how many of them can be shown at once
		maxMatches: number;
	};
};

const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv']);

/**
 * Lowercase words without accents or punctuation: "Robert F. Kennedy Jr." gives robert, f, kennedy, jr
 */
function toExactWords(value: string): string[] {
	return removeDiacritics(value.toLowerCase())
		.replace(/[^\p{L}\p{N}\s]/gu, '')
		.split(/\s+/)
		.filter(Boolean);
}

/**
 * The words without a trailing suffix too, for matching a query: "Robert F. Kennedy Jr." gives robert, f, kennedy
 */
function toWords(value: string): string[] {
	const words = toExactWords(value);

	while (words.length > 1 && NAME_SUFFIXES.has(words.at(-1) ?? '')) words.pop();
	return words;
}

/**
 * The first and last word with any middle words that aren't initials: "Donald J Trump" and "donald trump" compare equal
 */
function toNameKey(words: string[]): string {
	if (words.length < 2) return words.join(' ');
	const middleWords = words.slice(1, -1).filter((w) => w.length > 1);
	return [words[0], ...middleWords, words.at(-1)].join(' ');
}

/**
 * Whether a candidate's name is the query's name. The lookup asks Typesense to match every query word against the name,
 * exactly or through a nickname synonym ("greg" for "Gregory"), but not in order. So the first name may differ (that's
 * where a nickname is), while the other words must match in order: "greg abbott" is "Gregory Abbott", but
 * "john paul smith" isn't "Paul John Smith", and neither is "Gregory Wayne Abbott" or "Abbott Greg".
 */
function isSameName(nameWords: string[], queryWords: string[]): boolean {
	const nameKey = toNameKey(nameWords).split(' ');
	const queryKey = toNameKey(queryWords).split(' ');
	return nameKey.length === queryKey.length && nameKey.slice(1).join(' ') === queryKey.slice(1).join(' ');
}

function byPetitionsDesc(a: DecisionMakerHitData, b: DecisionMakerHitData): number {
	return b.petition_count - a.petition_count;
}

function isLinkable(dm: DecisionMakerHitData): dm is LinkableDecisionMakerHitData {
	return !!dm.slug;
}

/**
 * The most petitioned candidate, if it has at least `minLeadRatio` times the petitions of the next one
 */
function findClearLeader(candidates: DecisionMakerHitData[], minLeadRatio: number): DecisionMakerHitData | undefined {
	const sorted = [...candidates].sort(byPetitionsDesc);
	const top = sorted.at(0);
	const next = sorted.at(1);
	if (!top) return undefined;
	if (next && top.petition_count < next.petition_count * minLeadRatio) return undefined;
	return top;
}

/**
 * Several people can share a full name ("john smith"): a verified one wins, otherwise one must clearly lead
 */
function matchFullName(
	queryWords: string[],
	candidates: DecisionMakerHitData[],
	{ fullName }: DecisionMakerMatchRules,
): DecisionMakerHitData | undefined {
	const namesakes = candidates.filter((dm) => isSameName(toWords(dm.name), queryWords));
	const verified = namesakes.filter((dm) => dm.verified_at);

	return findClearLeader(verified.length ? verified : namesakes, fullName.minLeadRatio);
}

/**
 * One record per name: duplicates of the same person ("Zohran Mamdani" twice) shouldn't count as rivals.
 * Names must be the same down to initials and suffixes, since "Alex J. Smith" and "Alex M. Smith", or
 * "Robert F. Kennedy" and "Robert F. Kennedy Jr.", are different people.
 * A verified record represents the name, otherwise the most petitioned one.
 */
function oneRecordPerName(candidates: DecisionMakerHitData[]): DecisionMakerHitData[] {
	const byName = new Map<string, DecisionMakerHitData>();
	for (const dm of candidates) {
		const nameKey = toExactWords(dm.name).join(' ');
		const current = byName.get(nameKey);
		const isBetter =
			!current ||
			(!!dm.verified_at && !current.verified_at) ||
			(!!dm.verified_at === !!current.verified_at && dm.petition_count > current.petition_count);
		if (isBetter) byName.set(nameKey, dm);
	}
	return [...byName.values()];
}

/**
 * A surname alone is ambiguous, so it only names someone well known: the most petitioned namesake must qualify,
 * and comes with the other qualifying namesakes close behind it, if any ("kelly": Laura and Mark Kelly).
 * A close namesake who is unverified or has no profile could be the person meant, so it means no card at all;
 * one who is verified but has fewer petitions is just left out.
 */
function matchSurname(
	surname: string,
	candidates: DecisionMakerHitData[],
	{ surnameOnly }: DecisionMakerMatchRules,
): DecisionMakerMatch | null {
	const namesakes = oneRecordPerName(
		candidates.filter((dm) => {
			const words = toWords(dm.name);
			return (
				words.at(-1) === surname &&
				toNameKey(words).split(' ').length <= surnameOnly.maxNameWords &&
				surnameOnly.targetableTypes.includes(dm.targetable_type ?? '')
			);
		}),
	).sort(byPetitionsDesc);
	const top = namesakes.at(0);
	if (!top) return null;

	const close = namesakes.filter((dm) => dm.petition_count * surnameOnly.minLeadRatio >= top.petition_count);
	const isKnown = (dm: DecisionMakerHitData): dm is LinkableDecisionMakerHitData =>
		isLinkable(dm) && (!surnameOnly.requireVerified || !!dm.verified_at);
	const qualifies = (dm: DecisionMakerHitData): dm is LinkableDecisionMakerHitData =>
		isKnown(dm) && dm.petition_count >= surnameOnly.minPetitions;
	if (!qualifies(top) || !close.every(isKnown)) return null;

	return { decisionMakers: close.filter(qualifies).slice(0, surnameOnly.maxMatches), matchType: 'surname' };
}

/**
 * The query as sent to Typesense, which requires every word: without middle initials or a suffix, so
 * "donald j trump" still finds a "Donald Trump" record (the matcher ignores them on the record's side too)
 */
export function toDecisionMakerLookupQuery(query: string): string {
	return toNameKey(toWords(query));
}

/**
 * Whether a query could name a decision maker at all, so the lookup can be skipped otherwise
 */
export function isDecisionMakerQuery(query: string, { maxQueryWords }: DecisionMakerMatchRules): boolean {
	const wordCount = toWords(query).length;
	return wordCount > 0 && wordCount <= maxQueryWords;
}

/**
 * The decision makers the query names among the search candidates, if any. Only linkable decision makers are
 * returned, but namesakes without a profile still count: against a full name's lead, or as a close namesake that
 * means no card for a surname.
 */
export function matchDecisionMaker(
	query: string,
	candidates: DecisionMakerHitData[],
	rules: DecisionMakerMatchRules,
): DecisionMakerMatch | null {
	if (!isDecisionMakerQuery(query, rules)) return null;

	const queryWords = toWords(query);
	if (queryWords.length === 1) return matchSurname(queryWords[0], candidates, rules);

	const match = matchFullName(queryWords, candidates, rules);
	return match && isLinkable(match) ? { decisionMakers: [match], matchType: 'name' } : null;
}
